import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  FiSearch,
  FiUser,
  FiFilter,
  FiDownload,
  FiAlertCircle,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import * as userService from '../../services/users';
import '../../styles/main.scss';

const AuditLogs = () => {
  const { user } = useAuth();

  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });
  const [localFilters, setLocalFilters] = useState({
    severity: '',
    status: '',
  });

  const {
    data: usersResult,
    isLoading: usersLoading,
    isError: usersError,
  } = useQuery(
    ['audit-users', userSearch],
    () =>
      userService.searchUsers({
        q: userSearch || undefined,
        limit: 50,
      }),
    {
      keepPreviousData: true,
    }
  );

  const {
    data: logsResult,
    isLoading: logsLoading,
    isError: logsError,
  } = useQuery(
    ['audit-logs', selectedUserId, pagination],
    () =>
      selectedUserId
        ? userService.getUserActivities(selectedUserId, pagination)
        : Promise.resolve({
            logs: [],
            total: 0,
            page: 1,
            totalPages: 1,
          }),
    {
      keepPreviousData: true,
      onError: (error) => {
        if (error?.message) {
          toast.error(error.message);
        }
      },
    }
  );

  const users = usersResult?.users || [];
  const logs = logsResult?.logs || [];
  const totalPages = logsResult?.totalPages || 1;

  const filteredLogs = logs.filter((log) => {
    if (localFilters.severity && log.severity !== localFilters.severity) {
      return false;
    }
    if (localFilters.status && log.status !== localFilters.status) {
      return false;
    }
    return true;
  });

  const handleUserChange = (id) => {
    setSelectedUserId(id);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleExportCsv = () => {
    if (!filteredLogs.length) {
      toast.error('No logs to export');
      return;
    }

    const headers = [
      'Timestamp',
      'User',
      'Action',
      'Entity Type',
      'Entity Name',
      'Status',
      'Severity',
      'IP Address',
    ];

    const rows = filteredLogs.map((log) => [
      new Date(log.createdAt).toISOString(),
      log.userId?.name || user?.name || 'Unknown',
      log.action,
      log.entityType,
      log.entityName || '',
      log.status,
      log.severity,
      log.ipAddress || '',
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'audit-logs.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="audit-logs-page">
      <div className="page-header">
        <div>
          <h1>Audit Logs</h1>
          <p>Review system activities and security events across users</p>
        </div>
        <button
          className="btn btn-outline"
          onClick={handleExportCsv}
          disabled={!filteredLogs.length}
        >
          <FiDownload />
          Export CSV
        </button>
      </div>

      <div className="page-content">
        <div className="audit-layout">
          <motion.div
            className="audit-sidebar"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h3>Select User</h3>
            <p>Choose a user to view their activity history</p>

            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>

            <div className="user-list">
              {usersLoading ? (
                <div className="loading-placeholder">
                  <div className="skeleton skeleton-text" />
                  <div className="skeleton skeleton-text" />
                  <div className="skeleton skeleton-text" />
                </div>
              ) : usersError ? (
                <div className="empty-state">
                  <FiAlertCircle className="empty-icon" />
                  <p>Failed to load users</p>
                </div>
              ) : users.length > 0 ? (
                users.map((u) => (
                  <button
                    key={u._id}
                    className={`user-item ${
                      selectedUserId === u._id ? 'active' : ''
                    }`}
                    onClick={() => handleUserChange(u._id)}
                  >
                    <div className="user-avatar">
                      {u.avatar ? (
                        <img src={u.avatar} alt={u.name} />
                      ) : (
                        <span>{u.name?.charAt(0)}</span>
                      )}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{u.name}</div>
                      <div className="user-meta">
                        <span>{u.email}</span>
                        {u.role && <span className="badge">{u.role}</span>}
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="empty-state">
                  <FiUser className="empty-icon" />
                  <p>No users found</p>
                </div>
              )}
            </div>

            <div className="filters">
              <h4>
                <FiFilter /> Filters
              </h4>
              <div className="form-group">
                <label>Severity</label>
                <select
                  className="form-control"
                  value={localFilters.severity}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      severity: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="info">Info</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-control"
                  value={localFilters.status}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="success">Success</option>
                  <option value="failure">Failure</option>
                  <option value="partial">Partial</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="audit-main"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {logsLoading && selectedUserId ? (
              <div className="loading-placeholder">
                <div className="skeleton skeleton-text" />
                <div className="skeleton skeleton-text" />
                <div className="skeleton skeleton-text" />
              </div>
            ) : !selectedUserId ? (
              <div className="empty-state">
                <FiFilter className="empty-icon" />
                <p>Select a user to view their audit logs</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="empty-state">
                <FiAlertCircle className="empty-icon" />
                <p>No logs found for the selected filters</p>
              </div>
            ) : (
              <>
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Action</th>
                        <th>Entity</th>
                        <th>Status</th>
                        <th>Severity</th>
                        <th>IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log) => (
                        <tr key={log._id}>
                          <td>
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td>
                            <div className="log-action">
                              <span className="log-action-name">
                                {log.action}
                              </span>
                              {log.actionType && (
                                <span className="badge subtle">
                                  {log.actionType}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="log-entity">
                              <span>{log.entityName || '-'}</span>
                              <span className="text-muted">
                                {log.entityType}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className={`badge status-${log.status}`}>
                              {log.status}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge severity-${log.severity}`}
                            >
                              {log.severity}
                            </span>
                          </td>
                          <td>{log.ipAddress || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="table-pagination">
                  <button
                    className="btn btn-outline"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Page {pagination.page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === totalPages}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
